import React, { useMemo, useState } from 'react';
import './kyc-questionnaire.css';

const Q2_OPTIONS = [
  'Scrap yard',
  'Junk yard',
  'Auto recycler',
  'Muffler shop',
  'Individuals',
  'Other',
];

const QUESTION_COPY = [
  {
    id: 1,
    type: 'years_text',
    title:
      'How long have you been in the catalytic converter purchasing business? Describe your knowledge about legal sourcing requirements.',
  },
  {
    id: 2,
    type: 'multi',
    title: 'Where do you purchase your catalytic converters from? (Check all that apply)',
  },
  {
    id: 3,
    type: 'yesno_text',
    title: 'Have you ever knowingly purchased stolen or unlawfully obtained catalytic converters?',
  },
  {
    id: 4,
    type: 'yesno_text',
    title:
      'Are you aware of and do you comply with all federal, state, and local record-keeping requirements for purchasing catalytic converters?',
  },
  {
    id: 5,
    type: 'yesno_text',
    title:
      'Do you make cash payments for catalytic converters and, if so, do you comply with anti-money laundering regulations?',
  },
  {
    id: 6,
    type: 'yesno_text',
    title: 'Do you maintain records of all cash transactions related to catalytic converter purchases?',
  },
  {
    id: 7,
    type: 'yesno_text',
    title:
      'Have you or any member of your organization ever been arrested or convicted of a crime related to theft, fraud, or dealing in stolen property?',
  },
  {
    id: 8,
    type: 'yesno_text',
    title:
      'Have you ever been denied service by any catalytic converter processor or smelter in the past 12 months?',
  },
];

function pick(obj, keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

function toYesNo(v) {
  if (v === true || v === 1) return 'yes';
  if (v === false || v === 0) return 'no';
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'y' || s === 'yes') return 'yes';
    if (s === 'n' || s === 'no') return 'no';
  }
  return null;
}

function formatSubmittedDate(raw) {
  if (raw === undefined || raw === null || raw === '') return null;
  const d =
    raw instanceof Date
      ? raw
      : typeof raw === 'number'
      ? new Date(raw > 1e12 ? raw : raw * 1000)
      : new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function optionIsSelected(selectedSet, opt) {
  for (const s of selectedSet) {
    if (s.toLowerCase() === opt.toLowerCase()) return true;
  }
  return false;
}

/**
 * Maps GET /read/v1/user/kyc/response payload:
 * yearsInBusiness, legalSourceKnowledge, secondQuery (1-based indices into Q2_OPTIONS),
 * thirdQuery…eighthQuery (+ optional thirdQueryDesc…eighthQueryDesc),
 * fullName, businessName, createdDate (ms), etc.
 */
function buildKycViewModel(raw, userContext) {
  const root = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};

  const q1Years = root.yearsInBusiness != null && root.yearsInBusiness !== '' ? String(root.yearsInBusiness) : '';
  const q1Text =
    root.legalSourceKnowledge != null && root.legalSourceKnowledge !== ''
      ? String(root.legalSourceKnowledge)
      : '';

  const q2Set = new Set();
  if (Array.isArray(root.secondQuery)) {
    root.secondQuery.forEach((idx) => {
      const n = Number(idx);
      if (Number.isInteger(n) && n >= 1 && n <= Q2_OPTIONS.length) {
        q2Set.add(Q2_OPTIONS[n - 1]);
      }
    });
  }

  const ynDetail = (yesKey, descKey) => ({
    yn: toYesNo(root[yesKey]),
    detail:
      root[descKey] != null && root[descKey] !== ''
        ? String(root[descKey])
        : '',
  });

  const submittedLabel = formatSubmittedDate(root.createdDate);

  const ownerFromUser =
    userContext && [userContext.firstName, userContext.lastName].filter(Boolean).join(' ').trim();
  const companyFromUser =
    userContext && (userContext.companyName || userContext.company_name || userContext.businessName || '');

  const certOwner =
    (root.fullName != null && String(root.fullName).trim()) ||
    pick(root, ['certificationOwnerName', 'ownerName']) ||
    ownerFromUser ||
    '';
  const certBusiness =
    (root.businessName != null && String(root.businessName).trim()) ||
    pick(root, ['certificationBusinessName', 'kycBusinessName', 'companyName']) ||
    companyFromUser ||
    '';

  const ackRaw = pick(root, ['acknowledged', 'certificationAcknowledged', 'kycAcknowledged', 'certAcknowledged']);
  let certAck = '—';
  if (ackRaw === true || ackRaw === 1) certAck = 'Yes';
  else if (ackRaw === false || ackRaw === 0) certAck = 'No';
  else if (ackRaw !== undefined && ackRaw !== null && String(ackRaw).trim() !== '') {
    certAck = String(ackRaw).trim();
  }

  return {
    submittedLabel,
    q1Years,
    q1Text,
    q2Selected: q2Set,
    q3: ynDetail('thirdQuery', 'thirdQueryDesc'),
    q4: ynDetail('fourthQuery', 'fourthQueryDesc'),
    q5: ynDetail('fifthQuery', 'fifthQueryDesc'),
    q6: ynDetail('sixthQuery', 'sixthQueryDesc'),
    q7: ynDetail('seventhQuery', 'seventhQueryDesc'),
    q8: ynDetail('eighthQuery', 'eighthQueryDesc'),
    certification: {
      ownerName: String(certOwner || '').trim() || '—',
      businessName: String(certBusiness || '').trim() || '—',
      acknowledged: certAck,
    },
  };
}

function DocIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 12h8v2H8v-2zm0 4h8v2H8v-2z"
      />
    </svg>
  );
}

function Chevron({ open }) {
  return <span className="kyc-q__chevron">{open ? '▲' : '▼'}</span>;
}

function ReadOnlyInput({ multiline, value }) {
  const common = {
    className: `kyc-q__input${multiline ? ' kyc-q__input--textarea' : ''}`,
    readOnly: true,
    value: value || '',
  };
  if (multiline) {
    return <textarea {...common} rows={4} />;
  }
  return <input type="text" {...common} />;
}

function YesNoReadOnly({ value }) {
  const yn = value === 'yes' || value === 'no' ? value : null;
  return (
    <div className="kyc-q__yn-row">
      <div className="kyc-q__yn">
        <span className={`kyc-q__radio${yn === 'yes' ? ' kyc-q__radio--on' : ''}`}>
          {yn === 'yes' ? <span className="kyc-q__radio-dot" /> : null}
        </span>
        Yes
      </div>
      <div className="kyc-q__yn">
        <span className={`kyc-q__radio${yn === 'no' ? ' kyc-q__radio--on' : ''}`}>
          {yn === 'no' ? <span className="kyc-q__radio-dot" /> : null}
        </span>
        No
      </div>
    </div>
  );
}

function MultiOptionReadOnly({ selectedSet }) {
  return (
    <div className="kyc-q__options">
      {Q2_OPTIONS.map((opt) => {
        const on = optionIsSelected(selectedSet, opt);
        return (
          <div key={opt} className="kyc-q__option-row">
            <span className={`kyc-q__check${on ? ' kyc-q__check--on' : ''}`}>
              {on ? <span className="kyc-q__check-dot" /> : null}
            </span>
            {opt}
          </div>
        );
      })}
    </div>
  );
}

function QuestionCard({ num, title, expanded, onToggle, children }) {
  return (
    <div className="kyc-q__card">
      <button type="button" className="kyc-q__card-head" onClick={onToggle} aria-expanded={expanded}>
        <span className="kyc-q__badge">{num}</span>
        <span className="kyc-q__card-title">{title}</span>
        <Chevron open={expanded} />
      </button>
      {expanded ? <div className="kyc-q__card-body">{children}</div> : null}
    </div>
  );
}

export default function KycQuestionnaireView({ data, userContext }) {
  const model = useMemo(() => buildKycViewModel(data, userContext), [data, userContext]);
  const [open, setOpen] = useState(() => {
    const o = {};
    for (let i = 1; i <= 8; i += 1) o[i] = true;
    return o;
  });

  const toggle = (id) => {
    setOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const hasAny =
    model.q1Years ||
    model.q1Text ||
    model.q2Selected.size > 0 ||
    model.q3.yn ||
    model.q4.yn ||
    model.q5.yn ||
    model.q6.yn ||
    model.q7.yn ||
    model.q8.yn ||
    model.submittedLabel ||
    (data && typeof data === 'object' && data.kycId);

  return (
    <div className="kyc-q">
      {model.submittedLabel ? (
        <div className="kyc-q__banner" role="status">
          <DocIcon className="kyc-q__banner-icon" />
          <span>Submitted on {model.submittedLabel}</span>
        </div>
      ) : null}

      <h2 className="kyc-q__title">KYC Questionnaire</h2>

      <QuestionCard
        num={1}
        title={QUESTION_COPY[0].title}
        expanded={open[1]}
        onToggle={() => toggle(1)}
      >
        <ReadOnlyInput value={model.q1Years} />
        <ReadOnlyInput multiline value={model.q1Text} />
      </QuestionCard>

      <QuestionCard
        num={2}
        title={QUESTION_COPY[1].title}
        expanded={open[2]}
        onToggle={() => toggle(2)}
      >
        <MultiOptionReadOnly selectedSet={model.q2Selected} />
      </QuestionCard>

      {[3, 4, 5, 6, 7, 8].map((n) => {
        const block = model[`q${n}`];
        const copy = QUESTION_COPY[n - 1];
        return (
          <QuestionCard key={n} num={n} title={copy.title} expanded={open[n]} onToggle={() => toggle(n)}>
            <YesNoReadOnly value={block.yn} />
            <ReadOnlyInput multiline value={block.detail} />
          </QuestionCard>
        );
      })}

      <div className="kyc-q__cert">
        <div className="kyc-q__cert-head">
          <DocIcon className="kyc-q__cert-icon" />
          <span className="kyc-q__cert-title">Certification</span>
        </div>
        <div className="kyc-q__cert-grid">
          <div>
            <div className="kyc-q__cert-field-label">Owner name</div>
            <div className="kyc-q__cert-field-value">{model.certification.ownerName}</div>
          </div>
          <div>
            <div className="kyc-q__cert-field-label">Business name</div>
            <div className="kyc-q__cert-field-value">{model.certification.businessName}</div>
          </div>
          <div>
            <div className="kyc-q__cert-field-label">Acknowledged</div>
            <div className="kyc-q__cert-field-value">{model.certification.acknowledged}</div>
          </div>
        </div>
      </div>

      {!hasAny ? (
        <p className="kyc-q__empty">No KYC questionnaire data in this response.</p>
      ) : null}

      <details className="kyc-q__raw">
        <summary>Raw KYC response (debug)</summary>
        <pre className="log-user-detail-json" style={{ marginTop: 8, marginBottom: 0 }}>
          {typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data)}
        </pre>
      </details>
    </div>
  );
}
